import { useState } from 'react';
import { trpc } from '../utils/trpc';
import styles from '../styles/Notificationsender.module.css';

type NotificationType = 'info' | 'venue_invite' | 'venue_post';

interface NotificationSenderProps {
  groupId?: string;
  defaultType?: NotificationType;
  onSent?: () => void;
}

/**
 * A card component permitting venue-sent notifications from authenticated venues
 * @param groupId specific target group id
 * @param defaultType notification type, defaults to 'info'
 * @param onSent custome send behavior
 * @returns notification card
 */
export default function VenueNotificationSender({
  groupId,
  defaultType = 'info',
  onSent,
}: NotificationSenderProps) {
  // Setup use references
  const [recipient, setRecipient] = useState('');
  const [type, setType] = useState<NotificationType>(defaultType);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // Handle successful send feedback
  const onSuccess = () => {
    setStatus('success');
    setRecipient('');
    setMessage('');
    setTimeout(() => setStatus('idle'), 3000);
    onSent?.();
  };

  // Handle failure to send feedback
  const onError = (err: { message: string }) => {
    setStatus('error');
    setErrorMsg(err.message);
    setTimeout(() => setStatus('idle'), 4000);
  };

  // Initialize notification mutation
  const sendGroup = trpc.notification.sendToGroupAdmins.useMutation({
    onSuccess,
    onError,
  });

  // Intialize pending status
  const isPending = sendGroup.isPending || sendGroup.isPending;

  // Handle trpc call to send message
  const handleSend = () => {
    // Formatting
    if (!recipient.trim() || !message.trim()) return;
    setStatus('idle');
    setErrorMsg('');

    // Call specific mutation
    sendGroup.mutate({
      groupId: recipient.trim(),
      type: type,
      message: message.trim(),
      ...(groupId && { groupId }),
    });
  };

  // Initialize key-value pairs mapping buttons to behaviors
  const TYPES: { value: NotificationType; label: string }[] = [
    { value: 'info', label: 'Info' },
    { value: 'venue_invite', label: 'Venue Invite' },
    { value: 'venue_post', label: 'Venue Post' },
  ];

  // Standard display
  return (
    <div className={styles.sender}>
      <div className={styles.header}>
        {/* Card Title */}
        <div className={styles.headerDot} />
        <h3 className={styles.headerTitle}>Send Notification</h3>
      </div>

      <div className={styles.body}>
        {/* Recipient */}
        <div className={styles.field}>
          <label className={styles.label}>Group Id</label>
          <input
            className={styles.input}
            placeholder="1234567890"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
          />
        </div>

        {/* Type */}
        <div className={styles.field}>
          <label className={styles.label}>Type</label>
          <div className={styles.typeRow}>
            {TYPES.map(({ value, label }) => (
              <button
                key={value}
                className={`${styles.typeBtn} ${type === value ? styles.typeActive : ''}`}
                onClick={() => setType(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Message */}
        <div className={styles.field}>
          <label className={styles.label}>Message</label>
          <textarea
            className={styles.textarea}
            placeholder="Write a message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
          />
        </div>

        {/* Live feedback */}
        {status === 'success' && (
          <div className={styles.successBanner}>✓ Notification sent</div>
        )}
        {status === 'error' && (
          <div className={styles.errorBanner}>{errorMsg || 'Failed to send'}</div>
        )}

        {/* Send button */}
        <button
          className={styles.sendBtn}
          onClick={handleSend}
          disabled={!recipient.trim() || !message.trim() || isPending}
        >
          {isPending ? 'Sending...' : 'Send Notification'}
        </button>
      </div>
    </div>
  );
}
